import TimesheetReportGlobalHeader from './Header';
import { CustomApiClient } from '@jobcost-monorepo/crud';
import React, { useRef, useState } from 'react';
import {
  IReportGlobalTimesheetFetchParams,
  ITimesheetsJobToInvoice,
} from './types';
import TimesheetReportGlobalBody from './Body';
import { useTranslation } from '@jobcost-monorepo/i18n';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Loader,
} from '@jobcost-monorepo/ui';
import InvoiceForm from './InvoiceForm';
import ReactPDF from '@react-pdf/renderer';
import {
  ITimesheetsReportGlobal,
  ITimesheetsReportGlobalPrint,
  TimesheetGlobalPdf,
} from '@jobcost-monorepo/utils';
import SendForm from './SendForm';

export default function TimesheetReportGlobalPage() {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(false);
  const [start, setStart] = useState<string>('');
  const [end, setEnd] = useState<string>('');
  const [sendFormOpen, setSendFormOpen] = useState<boolean>(false);
  const [groupedDataPrint, setGroupedDataPrint] = useState<
    ITimesheetsReportGlobalPrint[]
  >([]);
  const [invoiceFormOpen, setInvoiceFormOpen] = useState<
    ITimesheetsJobToInvoice | undefined
  >();

  const handlePrint = () => {
    ReactPDF.pdf(
      <TimesheetGlobalPdf
        dataList={groupedDataPrint}
        startDate={start}
        endDate={end}
      />,
    )
      .toBlob()
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const iframe = document.createElement('iframe');
        iframe.style.position = 'absolute';
        iframe.style.width = '0px';
        iframe.style.height = '0px';
        iframe.style.border = 'none';
        document.body.appendChild(iframe);
        iframe.src = url;
        iframe.onload = () => {
          iframe.contentWindow?.focus();
          iframe.contentWindow?.print();
        };
      });
  };

  const handleSend = async () => {
    setSendFormOpen(true);
  };

  const handleSearch = async (
    startDate: string,
    endDate: string,
    withDetails: boolean,
    isEmployee?: boolean,
    resourceId?: number,
    customerId?: number,
    jobId?: number,
  ) => {
    setLoading(true);
    try {
      const props = {
        details: withDetails,
        startDate: startDate,
        endDate: endDate,
        isEmployee,
        resourceId,
        customerId,
        jobId,
      };

      const customApiClient = new CustomApiClient();
      const response = await customApiClient.getMany<
        ITimesheetsReportGlobal,
        IReportGlobalTimesheetFetchParams
      >('timesheets/reportGlobal', props);

      if (response.succeeded) {
        setStart(startDate);
        setEnd(endDate);
        setGroupedDataPrint(
          response.data?.data.map((data) => {
            return {
              data,
              print: true,
            };
          }) ?? [],
        );
      }
    } catch (error: any) {
      console.log(error);
    }
    setLoading(false);
  };

  return (
    <div className="flex flex-col overflow-hidden m-6 h-full">
      <TimesheetReportGlobalHeader
        handleSearch={handleSearch}
        handlePrint={handlePrint}
        handleSend={handleSend}
      />
      <Loader showLoader={loading} />
      {groupedDataPrint.length ? (
        <TimesheetReportGlobalBody
          setGroupedData={setGroupedDataPrint}
          ref={ref}
          data={groupedDataPrint}
          startDate={start}
          endDate={end}
          setInvoiceFormOpen={setInvoiceFormOpen}
        />
      ) : (
        <div className="size-full flex items-center justify-center">
          {t('noRecordFound')}
        </div>
      )}

      <Dialog open={sendFormOpen} onOpenChange={() => setSendFormOpen(false)}>
        <DialogTrigger asChild className="w-full"></DialogTrigger>
        <DialogTitle></DialogTitle>
        <DialogContent
          hasCloseIcon={false}
          className="relative transform overflow-y-hidden rounded-lg mx-5 border border-gray dark:border-lightGray bg-white dark:bg-dark px-4 pt-5 pb-4 text-left shadow-xl transition-all"
        >
          <SendForm
            setSendFormOpen={setSendFormOpen}
            groupedDataPrint={groupedDataPrint}
            start={start}
            end={end}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!invoiceFormOpen}
        onOpenChange={() => setInvoiceFormOpen(undefined)}
      >
        <DialogTrigger asChild className="w-full"></DialogTrigger>
        <DialogTitle></DialogTitle>
        <DialogContent
          hasCloseIcon={false}
          className="relative transform overflow-y-hidden rounded-lg mx-5 border border-gray dark:border-lightGray bg-white dark:bg-dark px-4 pt-5 pb-4 text-left shadow-xl transition-all"
        >
          {invoiceFormOpen && (
            <InvoiceForm
              setInvoiceFormOpen={setInvoiceFormOpen}
              jobToInvoice={invoiceFormOpen}
              startDate={start}
              endDate={end}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
